import React, { useState, useEffect, useCallback } from 'react'

import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import get from 'lodash/get'
import find from 'lodash/find'
import PropTypes from 'prop-types'
import { toast } from 'react-toastify'
import * as Sentry from 'Services/sentry'
import { ReactComponent as Download } from 'Assets/Icons/Descargar.svg'
import { ReactComponent as InProcessIcon } from 'Assets/Icons/clock-time-two-outline.svg'
import { ReactComponent as Approval } from 'Assets/Icons/Approval.svg'
import ArrowDropdown from 'Assets/Icons/ArrowDropdown'
import { getDraftOrderProducts, getDraftProviderOrdersExcel } from 'Services/api'
import { getApiErrorMessage } from 'Config/utils'
import { ROLES, PROVIDER_ORDERS_MODIFICATION_TYPE } from 'Config/constants'
import colors from 'Assets/colors'
import { NumberFormat } from 'Components/atoms'
import { financial } from 'Components/utils/cartCalculator'
import ProductInfoLoader from 'Components/atoms/productInfoLoader'
import ProductNameLoader from 'Components/atoms/productNameLoader'
import ProductActionLoader from 'Components/atoms/productActionLoader'
import ActionButton from 'Components/atoms/actionButton'
import ModalDownloadEmail from 'Components/molecules/modalDownloadEmail'

import {
  ProductsContainer,
  TableHeaderRow,
  TableHeaderItem,
  SingleProductRow,
  SingleProductRowCL,
  SingleProductNameHelper,
  SingleProductName,
  SingleProductPriceContainer,
  SingleProductQtyContainer,
  SingleProductTotalPrice,
  SingleProductTotalPriceSplit,
  SingleProductTotalQty,
  SingleProductButtonContainer,
  Price,
  PriceKG,
  MinAndMulti,
  RequestProviderInfoContainer,
  RequestProviderInfo,
  ProviderDetail,
  RequestOrderContainer,
  RequestRepeatOrderContainer,
  RequestActionContainer,
  RequestRepeatOrder,
  RequestActionOrderContainer,
  RequestActionOrder,
  HelpText,
  ShowProductsContainer,
  ShowMoreCopy,
  ShowLessCopy,
  SingleProductTotalQtyLabelMobile,
  SingleProductNameLabelMobile,
  ProviderProductsContainer,
  ProviderContainer,
  ProviderRow,
  TotalProviderRow,
} from './styled'

const DraftHistoryOrderProduct = ({ centerId, orderId }) => {
  const { t } = useTranslation()

  const [products, setProducts] = useState(null)

  const [productsLoading, setProductsLoading] = useState(false)
  const [openProducts, setOpenProducts] = useState(false)

  const [showDownloadProviderOrdersExcelModal, setShowDownloadProviderOrdersExcelModal] = useState(false)

  const user = useSelector(state => state.auth.user)
  const center = useSelector(state => state.auth.selectedCenter)
  const role = find(user.roles, elem => elem !== 'ROLE_USER')

  const { showPrice } = center

  const getGroupedProducts = useCallback(response => {
    const groupedByProvider = response.lines.reduce((acc, line) => {
      const providerId = line.provider.id
      const providerName = line.provider.name

      if (!acc[providerId]) {
        acc[providerId] = { providerId, providerName, lines: [], totalCount: 1, totalPrice: line.price }
      } else {
        const totalPr = acc[providerId].totalPrice + line.price
        const totalItems = acc[providerId].totalCount + 1
        acc[providerId] = { ...acc[providerId], totalCount: totalItems, totalPrice: totalPr }
      }

      acc[providerId].lines.push(line)
      return acc
    }, {})

    const result = Object.values(groupedByProvider).sort((a, b) => a.providerId - b.providerId)

    return result
  }, [])

  useEffect(() => {
    if (openProducts) {
      setProductsLoading(true)
      getDraftOrderProducts(centerId, orderId)
        .then(response => {
          const groupedProd = getGroupedProducts(response)
          setProducts(groupedProd)
        })
        .catch(error => {
          toast.error(getApiErrorMessage(error))
        })
        .finally(() => setProductsLoading(false))
    } else {
      setProducts(null)
    }
  }, [openProducts, centerId, orderId, getGroupedProducts])

  // eslint-disable-next-line react/prop-types
  const OrderState = () => {
    const stateProps = {
      icon: <InProcessIcon />,
      copy: t('draftOrderStatusDesc'),
    }

    const ActionOrder = () => (
      <RequestActionOrderContainer stateProp={stateProps} role={ROLES[role]} openProducts={openProducts[orderId]}>
        <RequestActionOrder stateProp={stateProps}>
          {stateProps.icon}
          {t('draftOrderStatus')}
        </RequestActionOrder>

        <HelpText>{stateProps.copy}</HelpText>
      </RequestActionOrderContainer>
    )

    if (stateProps.link && !ROLES[role]) {
      return (
        <Link to={stateProps.link} className='link'>
          <ActionOrder />
        </Link>
      )
    }
    return <ActionOrder />
  }

  const handleClickExcel = () => {
    getDraftProviderOrdersExcel(centerId, orderId)
      .then(() => setShowDownloadProviderOrdersExcelModal(true))
      .catch(e => {
        toast.error(t('petitionError'))
        Sentry.captureException(e)
      })
  }

  const handleClickSeeProducts = id => setOpenProducts({ ...openProducts, [id]: !openProducts[id] })

  return (
    <>
      <RequestProviderInfoContainer>
        <RequestProviderInfo />
        <ProviderDetail>
          <ShowProductsContainer onClick={() => handleClickSeeProducts(orderId)}>
            <ArrowDropdown
              fill={openProducts[orderId] ? colors.secondary : colors.black}
              style={{ margin: '0 0.5rem', transform: openProducts[orderId] ? 'rotate(180deg)' : 'none' }}
            />
            {openProducts[orderId] ? (
              <ShowLessCopy>{t('contractOrder')}</ShowLessCopy>
            ) : (
              <ShowMoreCopy>{t('seeOrderOpen')}</ShowMoreCopy>
            )}
          </ShowProductsContainer>
        </ProviderDetail>
      </RequestProviderInfoContainer>
      <RequestOrderContainer openProducts={openProducts[orderId]}>
        <RequestRepeatOrderContainer>
          <ActionButton
            onClick={handleClickExcel}
            buttonText={t('draftDownload')}
            helpText={t('draftDownloadDesc')}
            Icon={Download}
          />
        </RequestRepeatOrderContainer>
        <OrderState />
      </RequestOrderContainer>

      {openProducts[orderId] &&
        products &&
        products.map(providerProducts => (
          <ProviderContainer>
            <ProviderRow withPrice={showPrice}>
              <ProviderProductsContainer>{providerProducts.providerName}</ProviderProductsContainer>
              <TotalProviderRow>
                {showPrice && (
                  <ProviderProductsContainer>
                    <NumberFormat value={providerProducts.totalPrice} suffix='€' />
                  </ProviderProductsContainer>
                )}
                <ProviderProductsContainer>
                  {`${providerProducts.totalCount} ${providerProducts.totalCount <= 1 ? t('product') : t('products')}`}
                </ProviderProductsContainer>
              </TotalProviderRow>
            </ProviderRow>

            <ProductsContainer>
              {providerProducts.lines && (
                <TableHeaderRow>
                  <TableHeaderItem>{t('products')}</TableHeaderItem>
                  {showPrice && <TableHeaderItem>{t('sum')}</TableHeaderItem>}
                  <TableHeaderItem>{t('quantity')}</TableHeaderItem>
                </TableHeaderRow>
              )}
              {productsLoading && !products && (
                <SingleProductRowCL>
                  <SingleProductNameHelper>
                    <ProductNameLoader />
                  </SingleProductNameHelper>
                  <SingleProductPriceContainer>
                    <ProductInfoLoader />
                  </SingleProductPriceContainer>
                  <SingleProductQtyContainer>
                    <ProductInfoLoader />
                  </SingleProductQtyContainer>
                  <SingleProductButtonContainer>
                    <ProductActionLoader />
                  </SingleProductButtonContainer>
                </SingleProductRowCL>
              )}
              {providerProducts.lines &&
                get(providerProducts, 'lines', []).map(product => {
                  const { name, price, measureUnit, amount, priceUnit, product: productData } = product
                  return (
                    <SingleProductRow withPrice={showPrice}>
                      <SingleProductNameHelper>
                        <SingleProductName>
                          <SingleProductNameLabelMobile>{t('product')}:</SingleProductNameLabelMobile> {name}
                        </SingleProductName>
                      </SingleProductNameHelper>
                      {showPrice && (
                        <SingleProductPriceContainer>
                          <SingleProductNameLabelMobile>{t('price')}:</SingleProductNameLabelMobile>
                          <SingleProductTotalPrice>
                            <NumberFormat value={price} />
                          </SingleProductTotalPrice>
                          <SingleProductTotalPriceSplit>
                            <Price>
                              <NumberFormat value={priceUnit} suffix={`€/${measureUnit}`} />
                            </Price>
                            <PriceKG />
                          </SingleProductTotalPriceSplit>
                        </SingleProductPriceContainer>
                      )}
                      <SingleProductQtyContainer>
                        <SingleProductTotalQty>
                          <SingleProductTotalQtyLabelMobile>{t('quantity')}:</SingleProductTotalQtyLabelMobile>
                          {amount} {measureUnit}
                        </SingleProductTotalQty>
                        <div style={{ display: 'flex' }}>
                          {productData && (
                            <MinAndMulti>
                              {`${t('min')}: ${financial(productData.min)} / ${t('multi')}: ${financial(
                                productData.multiple
                              )}`}
                            </MinAndMulti>
                          )}
                        </div>
                      </SingleProductQtyContainer>
                    </SingleProductRow>
                  )
                })}
            </ProductsContainer>
          </ProviderContainer>
        ))}

      <ModalDownloadEmail
        setShowModal={() => setShowDownloadProviderOrdersExcelModal(false)}
        showModal={showDownloadProviderOrdersExcelModal}
        title={t('draftDownload')}
        description={t('draftDownloadDesc')}
      />
    </>
  )
}

DraftHistoryOrderProduct.propTypes = {
  centerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  orderId: PropTypes.string,
}

DraftHistoryOrderProduct.defaultProps = {
  centerId: null,
  orderId: null,
}

const SeeApprovedRequestAction = ({ onClick, type }) => {
  const { t } = useTranslation()

  const { MODIFIED, AUT } = PROVIDER_ORDERS_MODIFICATION_TYPE
  const isModified = type === MODIFIED
  const isAutomaticallyApproved = type === AUT

  let helperText = t('noModified')
  if (isModified) {
    helperText = t('modified')
  } else if (isAutomaticallyApproved) {
    helperText = t('isAutomaticallyApproved')
  }

  return (
    <RequestActionContainer>
      <RequestRepeatOrder>
        <Approval />
        <button type='button' onClick={onClick}>
          {t('requestOrderStatus.Aprobada')}
        </button>
      </RequestRepeatOrder>
      <HelpText bold={isModified}>{helperText}</HelpText>
    </RequestActionContainer>
  )
}

SeeApprovedRequestAction.propTypes = {
  onClick: PropTypes.func,
  type: PropTypes.string,
}

SeeApprovedRequestAction.defaultProps = {
  onClick: () => null,
  type: '',
}

export default DraftHistoryOrderProduct
